import { ChangeEvent, FormEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  ArrowLeft, Bug, Download, FileText, HelpCircle, Lightbulb, Loader2,
  MessageSquarePlus, Paperclip, RefreshCw, Send, SlidersHorizontal, X,
} from 'lucide-react'
import { useLocation } from 'react-router-dom'
import toast from 'react-hot-toast'

import {
  api, EmployeeFeedback, EmployeeFeedbackCategory, EmployeeFeedbackStatus,
} from '../api/api'
import { useAuth } from '../contexts/AuthContext'

const OWNER_EMAIL = 'vts@ecmit.ru'
const MAX_FILES = 5
const MAX_FILE_SIZE = 20 * 1024 * 1024
const ALLOWED_EXTENSIONS = ['png', 'jpg', 'jpeg', 'webp', 'pdf', 'docx', 'xlsx', 'txt', 'csv']

const categories: Array<{
  value: EmployeeFeedbackCategory
  label: string
  description: string
  icon: typeof Bug
}> = [
  { value: 'bug', label: 'Ошибка', description: 'Что-то работает неправильно', icon: Bug },
  { value: 'feature', label: 'Новая функция', description: 'Идея для приложения', icon: Lightbulb },
  { value: 'improvement', label: 'Доработка', description: 'Улучшение текущей функции', icon: SlidersHorizontal },
  { value: 'question', label: 'Вопрос', description: 'Нужна помощь или пояснение', icon: HelpCircle },
]

const categoryLabels: Record<EmployeeFeedbackCategory, string> = {
  bug: 'Ошибка', feature: 'Новая функция', improvement: 'Доработка', question: 'Вопрос',
}

const statusLabels: Record<EmployeeFeedbackStatus, string> = {
  new: 'Новое', in_progress: 'В работе', resolved: 'Решено', rejected: 'Отклонено',
}

const statusStyles: Record<EmployeeFeedbackStatus, string> = {
  new: 'bg-blue-100 text-blue-800 dark:bg-blue-950 dark:text-blue-200',
  in_progress: 'bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-200',
  resolved: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-200',
  rejected: 'bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-200',
}

const formatDate = (value: string) => new Date(value).toLocaleString('ru-RU', {
  day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
})

const formatSize = (value: number) => value >= 1024 * 1024
  ? `${(value / 1024 / 1024).toFixed(1)} МБ`
  : `${Math.max(1, Math.round(value / 1024))} КБ`

export default function FeedbackWidget() {
  const { user } = useAuth()
  const location = useLocation()
  const isOwner = (user?.email || '').toLowerCase() === OWNER_EMAIL
  const fileInputRef = useRef<HTMLInputElement | null>(null)

  const [open, setOpen] = useState(false)
  const [mode, setMode] = useState<'create' | 'inbox'>('create')
  const [category, setCategory] = useState<EmployeeFeedbackCategory>('bug')
  const [subject, setSubject] = useState('')
  const [body, setBody] = useState('')
  const [files, setFiles] = useState<File[]>([])
  const [submitting, setSubmitting] = useState(false)
  const [loading, setLoading] = useState(false)
  const [feedback, setFeedback] = useState<EmployeeFeedback[]>([])
  const [filter, setFilter] = useState<'all' | EmployeeFeedbackStatus>('all')
  const [selectedId, setSelectedId] = useState<number | null>(null)
  const [statusDraft, setStatusDraft] = useState<EmployeeFeedbackStatus>('new')
  const [noteDraft, setNoteDraft] = useState('')
  const [saving, setSaving] = useState(false)

  const selected = feedback.find((item) => item.id === selectedId) || null
  const newCount = feedback.filter((item) => item.status === 'new').length
  const filteredFeedback = useMemo(
    () => filter === 'all' ? feedback : feedback.filter((item) => item.status === filter),
    [feedback, filter],
  )

  const loadFeedback = useCallback(async (quiet = false) => {
    if (!isOwner) return
    if (!quiet) setLoading(true)
    try {
      const response = await api.listEmployeeFeedback()
      setFeedback(response.feedback || [])
    } catch (error: any) {
      if (!quiet) toast.error(error.message || 'Не удалось загрузить обращения')
    } finally {
      if (!quiet) setLoading(false)
    }
  }, [isOwner])

  useEffect(() => {
    if (!isOwner) return
    void loadFeedback(true)
    const timer = window.setInterval(() => void loadFeedback(true), 60000)
    return () => window.clearInterval(timer)
  }, [isOwner, loadFeedback])

  useEffect(() => {
    if (!selected) return
    setStatusDraft(selected.status)
    setNoteDraft(selected.admin_note || '')
  }, [selected?.id, selected?.status, selected?.admin_note])

  const addFiles = (incoming: File[]) => {
    const next = [...files]
    for (const file of incoming) {
      if (next.length >= MAX_FILES) {
        toast.error('Можно приложить не более 5 файлов')
        break
      }
      const extension = file.name.split('.').pop()?.toLowerCase() || ''
      if (!ALLOWED_EXTENSIONS.includes(extension)) {
        toast.error(`Файл «${file.name}» имеет неподдерживаемый формат`)
        continue
      }
      if (file.size > MAX_FILE_SIZE) {
        toast.error(`Файл «${file.name}» превышает 20 МБ`)
        continue
      }
      if (!next.some((item) => item.name === file.name && item.size === file.size)) next.push(file)
    }
    setFiles(next)
  }

  const handleFiles = (event: ChangeEvent<HTMLInputElement>) => {
    addFiles(Array.from(event.target.files || []))
    event.target.value = ''
  }

  const resetForm = () => {
    setCategory('bug')
    setSubject('')
    setBody('')
    setFiles([])
  }

  const submit = async (event: FormEvent) => {
    event.preventDefault()
    if (subject.trim().length < 3 || body.trim().length < 5) {
      toast.error('Заполните тему и описание')
      return
    }
    setSubmitting(true)
    try {
      await api.createEmployeeFeedback({
        category,
        subject: subject.trim(),
        body: body.trim(),
        page_url: window.location.href,
        user_agent: navigator.userAgent,
      }, files)
      toast.success('Обращение отправлено')
      resetForm()
      setOpen(false)
      if (isOwner) void loadFeedback(true)
    } catch (error: any) {
      toast.error(error.message || 'Не удалось отправить обращение')
    } finally {
      setSubmitting(false)
    }
  }

  const saveFeedback = async () => {
    if (!selected) return
    setSaving(true)
    try {
      const response = await api.updateEmployeeFeedback(selected.id, {
        status: statusDraft,
        admin_note: noteDraft.trim(),
      })
      setFeedback((current) => current.map((item) => item.id === selected.id ? response.feedback : item))
      toast.success('Обращение обновлено')
    } catch (error: any) {
      toast.error(error.message || 'Не удалось обновить обращение')
    } finally {
      setSaving(false)
    }
  }

  const downloadAttachment = async (feedbackId: number, attachmentId: number) => {
    try {
      const result = await api.downloadEmployeeFeedbackAttachment(feedbackId, attachmentId)
      const url = URL.createObjectURL(result.blob)
      const anchor = document.createElement('a')
      anchor.href = url
      anchor.download = result.filename
      anchor.click()
      URL.revokeObjectURL(url)
    } catch (error: any) {
      toast.error(error.message || 'Не удалось скачать вложение')
    }
  }

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="fixed bottom-3 left-3 z-40 flex h-11 items-center gap-2 rounded-md bg-blue-600 px-3 text-white shadow-lg hover:bg-blue-700 sm:bottom-4 sm:left-4"
        title="Сообщить об ошибке или предложить улучшение"
      >
        <MessageSquarePlus className="h-5 w-5" />
        <span className="hidden text-sm font-medium sm:inline">Обратная связь</span>
        {isOwner && newCount > 0 && <span className="absolute -right-2 -top-2 min-w-5 rounded-full bg-red-600 px-1 text-center text-xs leading-5">{newCount > 99 ? '99+' : newCount}</span>}
      </button>

      {open && (
        <div className="fixed inset-0 z-[70] flex items-end justify-center bg-black/40 sm:items-center sm:p-4" onClick={() => setOpen(false)}>
          <section className="flex h-[92dvh] w-full max-w-4xl flex-col overflow-hidden rounded-t-lg bg-white shadow-2xl dark:bg-gray-900 sm:h-[720px] sm:max-h-[90vh] sm:rounded-lg" onClick={(event) => event.stopPropagation()}>
            <header className="flex items-center gap-3 border-b border-gray-200 px-4 py-3 dark:border-gray-700">
              {mode === 'inbox' && selected && <button type="button" onClick={() => setSelectedId(null)} className="p-2 sm:hidden" aria-label="Назад к обращениям"><ArrowLeft className="h-5 w-5" /></button>}
              <MessageSquarePlus className="h-5 w-5 text-blue-600" />
              <div className="min-w-0 flex-1">
                <h2 className="font-semibold text-gray-950 dark:text-white">Обратная связь</h2>
                <p className="truncate text-xs text-gray-500">{location.pathname}</p>
              </div>
              <button type="button" onClick={() => setOpen(false)} className="p-2 text-gray-500 hover:text-gray-900 dark:hover:text-white" aria-label="Закрыть"><X className="h-5 w-5" /></button>
            </header>

            {isOwner && (
              <div className="flex border-b border-gray-200 px-4 dark:border-gray-700">
                <button type="button" onClick={() => { setMode('create'); setSelectedId(null) }} className={`border-b-2 px-3 py-2 text-sm ${mode === 'create' ? 'border-blue-600 text-blue-700 dark:text-blue-300' : 'border-transparent text-gray-500'}`}>Новое обращение</button>
                <button type="button" onClick={() => { setMode('inbox'); void loadFeedback() }} className={`relative border-b-2 px-3 py-2 text-sm ${mode === 'inbox' ? 'border-blue-600 text-blue-700 dark:text-blue-300' : 'border-transparent text-gray-500'}`}>Входящие{newCount > 0 ? ` · ${newCount}` : ''}</button>
              </div>
            )}

            {mode === 'create' ? (
              <form onSubmit={submit} className="min-h-0 flex-1 overflow-y-auto p-4 sm:p-5">
                <fieldset>
                  <legend className="text-sm font-medium text-gray-800 dark:text-gray-200">Тип обращения</legend>
                  <div className="mt-2 grid grid-cols-2 gap-2 sm:grid-cols-4">
                    {categories.map(({ value, label, description, icon: Icon }) => (
                      <button key={value} type="button" onClick={() => setCategory(value)} className={`min-w-0 rounded-md border p-3 text-left ${category === value ? 'border-blue-600 bg-blue-50 dark:bg-blue-950/30' : 'border-gray-200 dark:border-gray-700'}`}>
                        <Icon className={`h-5 w-5 ${category === value ? 'text-blue-600' : 'text-gray-500'}`} />
                        <div className="mt-2 text-sm font-medium text-gray-900 dark:text-white">{label}</div>
                        <div className="mt-1 hidden text-xs text-gray-500 sm:block">{description}</div>
                      </button>
                    ))}
                  </div>
                </fieldset>

                <label className="mt-5 block">
                  <span className="text-sm font-medium text-gray-800 dark:text-gray-200">Тема</span>
                  <input value={subject} onChange={(event) => setSubject(event.target.value)} maxLength={200} className="input mt-1" placeholder="Кратко опишите проблему или предложение" />
                </label>
                <label className="mt-4 block">
                  <span className="text-sm font-medium text-gray-800 dark:text-gray-200">Описание</span>
                  <textarea
                    value={body}
                    onChange={(event) => setBody(event.target.value)}
                    onPaste={(event) => {
                      const pastedFiles = Array.from(event.clipboardData.files || []).filter((file) => file.type.startsWith('image/'))
                      if (pastedFiles.length) addFiles(pastedFiles)
                    }}
                    maxLength={10000}
                    className="input mt-1 min-h-36 resize-y"
                    placeholder="Что вы делали, что ожидали увидеть и что произошло? Скриншот можно вставить из буфера прямо сюда."
                  />
                </label>

                <div className="mt-4">
                  <input ref={fileInputRef} type="file" className="hidden" multiple accept=".png,.jpg,.jpeg,.webp,.pdf,.docx,.xlsx,.txt,.csv" onChange={handleFiles} />
                  <button type="button" onClick={() => fileInputRef.current?.click()} className="btn btn-secondary inline-flex items-center gap-2"><Paperclip className="h-4 w-4" />Прикрепить файлы</button>
                  <span className="ml-2 text-xs text-gray-500">до 5 файлов, каждый до 20 МБ</span>
                  {files.length > 0 && <div className="mt-2 space-y-1">{files.map((file, index) => (
                    <div key={`${file.name}-${file.size}`} className="flex min-w-0 items-center gap-2 border border-gray-200 px-2 py-1.5 text-sm dark:border-gray-700">
                      <FileText className="h-4 w-4 shrink-0 text-gray-500" /><span className="min-w-0 flex-1 truncate">{file.name}</span><span className="shrink-0 text-xs text-gray-500">{formatSize(file.size)}</span>
                      <button type="button" onClick={() => setFiles((current) => current.filter((_, itemIndex) => itemIndex !== index))} className="p-1 text-gray-500 hover:text-red-600" aria-label="Удалить вложение"><X className="h-4 w-4" /></button>
                    </div>
                  ))}</div>}
                </div>

                <div className="mt-6 flex justify-end gap-2 border-t border-gray-200 pt-4 dark:border-gray-700">
                  <button type="button" className="btn btn-secondary" onClick={() => setOpen(false)}>Отмена</button>
                  <button type="submit" className="btn btn-primary inline-flex items-center gap-2" disabled={submitting}>{submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}{submitting ? 'Отправляю...' : 'Отправить'}</button>
                </div>
              </form>
            ) : (
              <div className="flex min-h-0 flex-1">
                <aside className={`${selected ? 'hidden sm:flex' : 'flex'} w-full min-w-0 flex-col border-r border-gray-200 dark:border-gray-700 sm:w-[340px]`}>
                  <div className="flex items-center gap-2 border-b border-gray-200 p-3 dark:border-gray-700">
                    <select value={filter} onChange={(event) => setFilter(event.target.value as typeof filter)} className="input !py-1.5 text-sm">
                      <option value="all">Все обращения</option><option value="new">Новые</option><option value="in_progress">В работе</option><option value="resolved">Решённые</option><option value="rejected">Отклонённые</option>
                    </select>
                    <button type="button" onClick={() => void loadFeedback()} className="p-2 text-gray-500 hover:text-blue-600" title="Обновить"><RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} /></button>
                  </div>
                  <div className="min-h-0 flex-1 overflow-y-auto">
                    {loading && !feedback.length ? <div className="p-6 text-center text-sm text-gray-500">Загрузка...</div> : filteredFeedback.length ? filteredFeedback.map((item) => (
                      <button key={item.id} type="button" onClick={() => setSelectedId(item.id)} className={`w-full border-b border-gray-100 p-3 text-left hover:bg-gray-50 dark:border-gray-800 dark:hover:bg-gray-800 ${selectedId === item.id ? 'bg-blue-50 dark:bg-blue-950/20' : ''}`}>
                        <div className="flex items-center gap-2"><span className="min-w-0 flex-1 truncate text-sm font-medium text-gray-900 dark:text-white">#{item.id} · {item.subject}</span><span className={`shrink-0 px-1.5 py-0.5 text-[11px] ${statusStyles[item.status]}`}>{statusLabels[item.status]}</span></div>
                        <div className="mt-1 truncate text-xs text-gray-500">{categoryLabels[item.category]} · {item.reporter_name}</div>
                        <div className="mt-1 text-xs text-gray-400">{formatDate(item.created_at)}</div>
                      </button>
                    )) : <div className="p-6 text-center text-sm text-gray-500">Обращений нет</div>}
                  </div>
                </aside>

                <main className={`${selected ? 'flex' : 'hidden sm:flex'} min-w-0 flex-1 flex-col`}>
                  {selected ? <div className="min-h-0 flex-1 overflow-y-auto p-4 sm:p-5">
                    <div className="flex flex-wrap items-start gap-2"><div className="min-w-0 flex-1"><div className="text-xs text-gray-500">#{selected.id} · {categoryLabels[selected.category]}</div><h3 className="mt-1 text-lg font-semibold text-gray-950 dark:text-white">{selected.subject}</h3></div><span className={`px-2 py-1 text-xs ${statusStyles[selected.status]}`}>{statusLabels[selected.status]}</span></div>
                    <div className="mt-3 text-sm text-gray-500">{selected.reporter_name} · {selected.reporter_email} · {formatDate(selected.created_at)}</div>
                    <p className="mt-4 whitespace-pre-wrap text-sm leading-6 text-gray-800 dark:text-gray-200">{selected.body}</p>
                    {selected.page_url && <a href={selected.page_url} target="_blank" rel="noreferrer" className="mt-3 block break-all text-sm text-blue-600 hover:underline dark:text-blue-400">{selected.page_url}</a>}
                    {selected.attachments.length > 0 && <div className="mt-4 space-y-2"><div className="text-sm font-medium">Вложения</div>{selected.attachments.map((attachment) => <button key={attachment.id} type="button" onClick={() => void downloadAttachment(selected.id, attachment.id)} className="flex w-full min-w-0 items-center gap-2 border border-gray-200 p-2 text-left text-sm hover:border-blue-400 dark:border-gray-700"><FileText className="h-4 w-4 shrink-0" /><span className="min-w-0 flex-1 truncate">{attachment.original_name}</span><span className="text-xs text-gray-500">{formatSize(attachment.size)}</span><Download className="h-4 w-4 shrink-0" /></button>)}</div>}
                    <div className="mt-6 grid gap-3 border-t border-gray-200 pt-4 dark:border-gray-700 sm:grid-cols-[180px_1fr]">
                      <label><span className="text-sm font-medium">Статус</span><select value={statusDraft} onChange={(event) => setStatusDraft(event.target.value as EmployeeFeedbackStatus)} className="input mt-1"><option value="new">Новое</option><option value="in_progress">В работе</option><option value="resolved">Решено</option><option value="rejected">Отклонено</option></select></label>
                      <label><span className="text-sm font-medium">Внутренняя заметка</span><textarea value={noteDraft} onChange={(event) => setNoteDraft(event.target.value)} className="input mt-1 min-h-24 resize-y" placeholder="Результат проверки, ссылка на коммит или причина решения" /></label>
                    </div>
                    <div className="mt-3 flex justify-end"><button type="button" onClick={() => void saveFeedback()} disabled={saving} className="btn btn-primary">{saving ? 'Сохраняю...' : 'Сохранить'}</button></div>
                  </div> : <div className="m-auto text-sm text-gray-500">Выберите обращение</div>}
                </main>
              </div>
            )}
          </section>
        </div>
      )}
    </>
  )
}
